import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import liaApi from 'config/api/lia/lia';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';

import {
  getLiaApiErrorMessage,
  isRetryableLiaApiError,
  mapFormValuesToUpdateInput,
  mapLiaToFormValues,
  type PurposeLiaFormValues,
} from './purposeLiaPage.helpers';

type Params = {
  onNavigate: () => void;
  onSaveSuccess?: () => void;
  onSoftDeleteSuccess?: () => void;
  organizationId?: number;
  purposeId: number;
};

const usePurposeLiaPage = ({ onNavigate, onSaveSuccess, onSoftDeleteSuccess, organizationId, purposeId }: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<PurposeLiaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.LIA_DETAIL, organizationId, purposeId],
    async () => {
      const { data } = await liaApi.getOrCreate(organizationId as number, purposeId);
      return data;
    },
    {
      enabled: Boolean(organizationId && purposeId),
      // Failures are reported on the page itself, so the global handler must not add its generic snackbar.
      onError: () => undefined,
      // Loading the LIA creates it when missing, so it must not be repeated behind the user's back.
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  useEffect(() => {
    if (detailQuery.data) {
      setFormValues(mapLiaToFormValues(detailQuery.data));
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [detailQuery.data]);

  const saveMutation = useMutation(
    MUTATION_KEYS.LIA_UPDATE,
    async () => {
      if (!formValues) return null;

      const { data } = await liaApi.update(organizationId as number, purposeId, mapFormValuesToUpdateInput(formValues));
      return data;
    },
    {
      onError: () => undefined,
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.LIA_DETAIL, organizationId, purposeId], data);
        setFormValues(mapLiaToFormValues(data));
        setIsDirty(false);
        onSaveSuccess?.();
      },
    },
  );

  const softDeleteMutation = useMutation(
    MUTATION_KEYS.LIA_SOFT_DELETE,
    async (deletionReason: string) => {
      const { data } = await liaApi.softDelete(organizationId as number, purposeId, {
        deletionReason,
      });
      return data;
    },
    {
      onError: () => undefined,
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.LIA_DETAIL, organizationId, purposeId], data);
        setFormValues(mapLiaToFormValues(data));
        setIsDirty(false);
        onSoftDeleteSuccess?.();
      },
    },
  );

  const handleSave = useCallback(async () => {
    await saveMutation.mutateAsync();
  }, [saveMutation]);

  const { NavigationPrompt, handleNavigation } = useUnsavedChangesProtection({
    isDirty,
    onNavigate,
    onSave: handleSave,
  });

  const patchFormValues = useCallback((patch: Partial<PurposeLiaFormValues>) => {
    setFormValues(current => {
      if (!current) return current;
      return { ...current, ...patch };
    });
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleSoftDelete = useCallback(
    async (deletionReason: string) => {
      await softDeleteMutation.mutateAsync(deletionReason);
    },
    [softDeleteMutation],
  );

  return {
    canRetryLoad: isRetryableLiaApiError(detailQuery.error),
    detail: detailQuery.data,
    formValues,
    handleNavigation,
    handleSave,
    handleSoftDelete,
    isDirty,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    isSaving: saveMutation.isLoading,
    isSoftDeleting: softDeleteMutation.isLoading,
    loadErrorMessage: getLiaApiErrorMessage(detailQuery.error),
    NavigationPrompt,
    patchFormValues,
    refetch: detailQuery.refetch,
    setFormValues,
    setIsDirty,
    setValidationErrors,
    validationErrors,
  };
};

export default usePurposeLiaPage;
